import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, CircularProgress, IconButton, Tooltip, Typography, alpha } from "@mui/material";
import { Add, Close, DeleteOutline, LockOutlined, PushPin, PushPinOutlined } from "@mui/icons-material";
import { useGetList, useGetMany, type Identifier } from "react-admin";
import { useQueryClient } from "@tanstack/react-query";
import { ResourceName, type DocumentRead } from "@/types";
import { waitForDocumentReady } from "@/resources/documents/hooks/useDocumentReady";
import { UIComponentSize } from "@/types/shared/uiComponents";
import Button from "@/components/ui/Button";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { composeDocumentLabel } from "@/utils/formatters/documentLabel";
import { formatFileSize, getFileExtension } from "@/utils/formatters/fileUtils";
import { DeleteImpactDialog } from "@/resources/documents/components/DeleteImpactDialog";
import { ATTACHMENT_RECORD_NOUN, ATTACHMENT_RECORD_SEGMENT, type AttachmentRecordType } from "./types";

interface DocumentsPanelProps {
  /** Record type this panel is attached to (ADR-086 target-type enum). */
  recordType: AttachmentRecordType;
  /** UUID of the parent record. */
  recordId: string;
  /**
   * The record's provenance documents (`source_documents`) — rendered read-only
   * in the "Source documents" section. Provenance is the cascade-delete input
   * and cannot be removed here (ADR-078 / ADR-086 D3).
   */
  sourceDocumentIds?: Identifier[];
  /** The document currently open in the preview drawer (highlights its row). */
  selectedDocumentId?: string | null;
  /** Select a document for preview, or `null` to close the preview. */
  onSelectDocument: (document: DocumentRead | null) => void;
  /**
   * The "pinned" (featured) document — the host's top-actions quick-open button
   * opens it, and its row shows a filled pin. The host owns the value and any
   * persistence (see usePinnedDocument). When nothing is pinned, the panel
   * auto-pins the first attachment (so the first document a user adds becomes
   * the default); a manual pin is never overridden.
   */
  pinnedDocumentId?: string | null;
  /** Set the pinned document (a row's pin toggle / first-attachment auto-pin). Omit to disable pinning. */
  onPin?: (document: DocumentRead) => void;
  /**
   * Clear the pin. Called when the pinned document is no longer in either
   * section — detached, deleted, or stored against a record that has since
   * changed — so a dangling pin can't outrank the fallback (ADR-087 D9). This is
   * garbage-collecting a pin whose target is gone, not overriding a live choice.
   */
  onClearPin?: () => void;
  /**
   * Renders a file uploader wired to attach on completion. The host owns the
   * record-type-specific upload params (carrier, client) and passes the panel's
   * attach handler as the completion callback. Omit to hide the add affordance.
   */
  renderUploader?: (onUploadComplete: (documentId: string) => Promise<void>) => ReactNode;
}

/**
 * Shared, record-type-parameterized documents surface (ADR-087). Presents both
 * document relations as one categorized list in the record body — read-only
 * **Source documents** (provenance) and add/removable **Attachments** — and
 * previews the selected document in the host's right drawer. Replaces the policy
 * `PolicyPDFView` chips + always-mounted GET + `localDocIdsRef` phantom-entry
 * path with a plain `useGetList` over the attachment relation (ADR-087 D5).
 */
export const DocumentsPanel = ({
  recordType,
  recordId,
  sourceDocumentIds,
  selectedDocumentId,
  onSelectDocument,
  pinnedDocumentId,
  onPin,
  onClearPin,
  renderUploader,
}: DocumentsPanelProps) => {
  const segment = ATTACHMENT_RECORD_SEGMENT[recordType];
  const recordNoun = ATTACHMENT_RECORD_NOUN[recordType];
  const attachmentsResource = `${segment}/${recordId}/attachments`;

  const queryClient = useQueryClient();
  // Both gates below mirror server-enforced permissions (#6562): attach checks
  // documents:create and DELETE /documents/{id}'s non-cascade path checks
  // documents:delete (HasPermission, 403 on failure), on top of PartnerWriteGuard
  // + ownership (404-masking). ADD gates on documents:create (the upload path's
  // permission — the attach flow always mints a fresh document — NOT {record}:create,
  // which is the wrong verb for editing an existing record) and DELETE on
  // documents:delete (the verb matching the operation). ADR-087 D7.
  const { canCreate: canManage, canDelete: canDeleteDocuments } = useResourcePermissions(ResourceName.DOCUMENTS);
  const [adding, setAdding] = useState(false);
  // Cancels an in-flight readiness wait if the panel unmounts mid-attach, so the
  // poll stops and we don't POST or setState on a dead component.
  const attachAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => attachAbortRef.current?.abort(), []);
  const [deleteTarget, setDeleteTarget] = useState<DocumentRead | null>(null);

  const PER_PAGE = 100;
  const {
    data: attachmentRows = [],
    total: attachmentTotal,
    isLoading: attachmentsLoading,
  } = useGetList(attachmentsResource, { pagination: { page: 1, perPage: PER_PAGE } }, { enabled: !!recordId });

  const attachmentDocIds = attachmentRows.map((row) => row.document_id as string).filter(Boolean);
  const sourceIds = (sourceDocumentIds ?? []).map(String);

  const { data: attachmentDocs = [], isLoading: attachmentDocsLoading } = useGetMany<DocumentRead>(
    ResourceName.DOCUMENTS,
    { ids: attachmentDocIds },
    { enabled: attachmentDocIds.length > 0 }
  );

  const { data: sourceDocs = [], isLoading: sourceLoading } = useGetMany<DocumentRead>(
    ResourceName.DOCUMENTS,
    { ids: sourceIds },
    { enabled: sourceIds.length > 0 }
  );

  // Auto-pin the first attachment when nothing is pinned yet — e.g. the user
  // just added the very first document, which should become the default the
  // drawer opens to. Guarded on pinnedDocumentId, so it never overrides a manual
  // pin. Safety against clobbering a *stored* pin on a warm cache does NOT come
  // from timing here — React flushes this child effect before the parent's — but
  // from usePinnedDocument rehydrating synchronously, so pinnedDocumentId is
  // already correct on the first render this effect sees (ADR-087 D9).
  const firstAttachment = attachmentDocs[0];
  const firstAttachmentId = firstAttachment ? String(firstAttachment.id) : undefined;
  useEffect(() => {
    if (onPin && !pinnedDocumentId && firstAttachment) onPin(firstAttachment);
    // firstAttachment/onPin read via closure; firstAttachmentId is the trigger.
  }, [pinnedDocumentId, firstAttachmentId]);

  // Reconcile a stale pin: drop it once loaded lists no longer contain its
  // target — detached (relation soft-deleted, document still live), deleted
  // (filtered from the list), or a pin stored on another device/record. Without
  // this the header quick-open opens a document no longer on the record, and the
  // stale pin outranks the source-document fallback (ADR-087 D9). Guarded on
  // loaded state so it can't wipe a valid pin mid-fetch; clearing then re-enables
  // auto-pin above.
  const listsLoaded = !attachmentsLoading && !attachmentDocsLoading && !sourceLoading;
  useEffect(() => {
    if (!onClearPin || !listsLoaded || !pinnedDocumentId) return;
    const present = [...attachmentDocs, ...sourceDocs].some((d) => String(d.id) === pinnedDocumentId);
    if (!present) onClearPin();
    // onClearPin read via closure; presence is derived from the loaded lists.
  }, [listsLoaded, pinnedDocumentId, attachmentDocs, sourceDocs]);

  // Invalidate the attachment relation so BOTH this list and the header/tab
  // presence check (usePolicyHasAttachments — same resource key) refresh.
  const invalidateAttachments = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [attachmentsResource] }),
    [queryClient, attachmentsResource]
  );

  // Attach: link an already-uploaded storage-only document to this record via
  // the first-class relation (POST), NOT a source_documents PATCH. Throws on
  // failure so the uploader reports it rather than showing a success toast.
  //
  // The upload returns 202 BEFORE its async finalize commits object_metadata
  // (#5347), and the attach handler rejects a document whose object_metadata
  // isn't storage-only yet (400). Attaching immediately therefore races the
  // finalize — the second, warm-path upload loses reliably. So wait for the
  // document to reach a committed status (object_metadata present) before the
  // POST; a soft-deleted/never-finalized doc still surfaces the real error.
  const handleAttach = useCallback(
    async (documentId: string) => {
      // Wait for the upload's async finalize to commit object_metadata before the
      // attach POST — the 202 precedes it and the attach handler 400s until then
      // (#5347). The shared waiter fails fast on a failed upload and cancels on
      // unmount rather than hanging the ceiling.
      attachAbortRef.current?.abort();
      const controller = new AbortController();
      attachAbortRef.current = controller;
      const outcome = await waitForDocumentReady(documentId, { signal: controller.signal });
      // Aborted (unmount / superseded) is a silent no-op. But a failed or
      // timed-out finalize means the attach POST would reliably 4xx — don't fire
      // it (that's how a failed attach + retry orphans a blob, #6528). Throw so
      // the uploader surfaces the reason instead of a misleading server error.
      if (outcome === "aborted" || controller.signal.aborted) return;
      if (outcome !== "ready") {
        throw new Error(
          outcome === "timeout"
            ? "The document didn’t finish processing in time. Please try again."
            : "The document couldn’t be prepared for attaching. Please try again."
        );
      }

      try {
        await httpClient(`${getPartnerAwareApiUrl()}/${segment}/${recordId}/attachments`, {
          method: "POST",
          body: JSON.stringify({ document_id: documentId }),
        });
      } catch (err) {
        // Surface the server's actual reason (Code/Message/correlation id) via the
        // rich notifier rather than a generic string — the generic wrapper hid the
        // cause. Attach errors carry no PHI (record/document ids and rule text
        // only), so re-throwing the raw API error is safe.
        console.error(`Error attaching document to ${recordNoun}:`, err);
        throw err;
      }
      await invalidateAttachments();
      setAdding(false);
    },
    [segment, recordId, recordNoun, invalidateAttachments]
  );

  // The row's trash **deletes the document** (via DeleteImpactDialog → DELETE
  // /documents/{id}), not just the attachment link. In this flow an attachment
  // is a fresh upload bound to one record, so "delete" is the honest verb. The
  // dialog runs in its plain mode (no cascade, no impact preview). After it
  // deletes, the document drops from the relation read (ADR-086 D3), so refresh
  // the list and tidy up preview + pin.
  //
  // DeleteImpactDialog deliberately leaves the list-refresh to the caller ("the
  // caller owns the side effect"), so besides the attachment relation we must
  // also invalidate the global `documents` resource — otherwise a soft-deleted
  // document lingers in the broader Documents list until a manual refresh (the
  // list API already filters it out server-side).
  const handleDeleted = useCallback(
    (documentId: string) => {
      void invalidateAttachments();
      void queryClient.invalidateQueries({ queryKey: [ResourceName.DOCUMENTS] });
      if (selectedDocumentId === documentId) onSelectDocument(null);
      if (pinnedDocumentId === documentId) onClearPin?.();
      setDeleteTarget(null);
    },
    [invalidateAttachments, queryClient, selectedDocumentId, onSelectDocument, pinnedDocumentId, onClearPin]
  );

  const showAdd = canManage && !!renderUploader;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Source documents — read-only provenance */}
      <DocumentsSection
        title="Source documents"
        helper="The system pulled this record’s data from these files."
        loading={sourceLoading}
        docs={sourceDocs}
        emptyText="No source documents for this record."
        locked
        selectedDocumentId={selectedDocumentId}
        onSelectDocument={onSelectDocument}
        pinnedDocumentId={pinnedDocumentId}
        onPin={onPin}
      />

      {/* Attachments — user-added, deletable */}
      <DocumentsSection
        title="Attachments"
        helper="Files added for reference — you can add or delete these."
        loading={attachmentsLoading || attachmentDocsLoading}
        docs={attachmentDocs}
        emptyText="No attachments yet."
        total={attachmentTotal}
        selectedDocumentId={selectedDocumentId}
        onSelectDocument={onSelectDocument}
        pinnedDocumentId={pinnedDocumentId}
        onPin={onPin}
        onDelete={canDeleteDocuments ? setDeleteTarget : undefined}
        headerAction={
          showAdd ? (
            <Button
              size={UIComponentSize.Small}
              variant="outlined"
              startIcon={adding ? <Close sx={{ fontSize: 14 }} /> : <Add sx={{ fontSize: 14 }} />}
              onClick={() => setAdding((prev) => !prev)}
            >
              {adding ? "Cancel" : "Add attachment"}
            </Button>
          ) : undefined
        }
      >
        {showAdd && adding ? <Box sx={{ mb: 1 }}>{renderUploader?.(handleAttach)}</Box> : null}
      </DocumentsSection>

      {/* The app's document-delete dialog, in its plain (allowCascade=false)
          mode: no cascade option and no impact sections — just a confirm. A
          single-record attachment never wants the cascade UI, and document-only
          delete never cascades, so the reprocessed-into-provenance edge stays
          safe (its created records simply remain). */}
      {deleteTarget && (
        <DeleteImpactDialog
          open
          allowCascade={false}
          documentId={String(deleteTarget.id)}
          documentName={composeDocumentLabel(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => handleDeleted(String(deleteTarget.id))}
        />
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Section
// ─────────────────────────────────────────────────────────────

interface DocumentsSectionProps {
  title: string;
  helper: string;
  loading: boolean;
  docs: DocumentRead[];
  emptyText: string;
  /**
   * Total count for the relation, if it can exceed what was fetched (attachments
   * page at PER_PAGE). When it exceeds `docs.length` the header says "showing X
   * of N" so a truncated list is visible, not silent. Omit for fully-fetched
   * sections (source documents).
   */
  total?: number;
  selectedDocumentId?: string | null;
  onSelectDocument: (document: DocumentRead | null) => void;
  pinnedDocumentId?: string | null;
  onPin?: (document: DocumentRead) => void;
  /** Read-only provenance section: render the lock affordance, no delete. */
  locked?: boolean;
  /** Delete the document (opens the cascade-aware delete dialog in the host). */
  onDelete?: (document: DocumentRead) => void;
  headerAction?: ReactNode;
  children?: ReactNode;
}

const DocumentsSection = ({
  title,
  helper,
  loading,
  docs,
  emptyText,
  total,
  selectedDocumentId,
  onSelectDocument,
  pinnedDocumentId,
  onPin,
  locked = false,
  onDelete,
  headerAction,
  children,
}: DocumentsSectionProps) => {
  const { theme } = useAppTheme();

  // Show a count; when the relation total exceeds what was fetched, say so
  // rather than truncating silently (e.g. "showing 100 of 143").
  const shown = docs.length;
  const truncated = total !== undefined && total > shown;
  const countLabel = loading ? null : truncated ? `showing ${shown} of ${total}` : String(total ?? shown);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1, gap: 2 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" fontWeight={700}>
              {title}
            </Typography>
            {countLabel !== null && (
              <Typography variant="caption" color={truncated ? "warning.main" : "text.secondary"}>
                {countLabel}
              </Typography>
            )}
            {locked && (
              <Tooltip title="This record’s data came from this file, so it can’t be removed here.">
                <LockOutlined sx={{ fontSize: 14, color: "text.disabled" }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        </Box>
        {headerAction}
      </Box>

      {children}

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : docs.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.disabled">
              {emptyText}
            </Typography>
          </Box>
        ) : (
          docs.map((doc, index) => (
            <DocumentRow
              key={String(doc.id)}
              doc={doc}
              selected={!!selectedDocumentId && String(doc.id) === String(selectedDocumentId)}
              pinned={!!pinnedDocumentId && String(doc.id) === String(pinnedDocumentId)}
              showBottomBorder={index < docs.length - 1}
              onClick={() => onSelectDocument(doc)}
              onPin={onPin ? () => onPin(doc) : undefined}
              onDelete={onDelete ? () => onDelete(doc) : undefined}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Row
// ─────────────────────────────────────────────────────────────

interface DocumentRowProps {
  doc: DocumentRead;
  selected: boolean;
  pinned: boolean;
  showBottomBorder: boolean;
  onClick: () => void;
  onPin?: () => void;
  onDelete?: () => void;
}

const DocumentRow = ({ doc, selected, pinned, showBottomBorder, onClick, onPin, onDelete }: DocumentRowProps) => {
  const { theme } = useAppTheme();
  const ext = getFileExtension(doc.name);
  const size = formatFileSize(doc.object_metadata?.content_length);
  const uploaded = doc.uploaded_datetime ? new Date(doc.uploaded_datetime).toLocaleDateString() : "—";

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-current={selected}
      aria-label={`Preview ${composeDocumentLabel(doc)}`}
      onClick={onClick}
      onKeyDown={(e) => {
        // Selecting a document is the feature (#6507 AC); keep it reachable by
        // keyboard, not just mouse. Enter/Space activate, matching a button.
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        bgcolor: selected ? "action.selected" : "transparent",
        borderLeft: selected ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
        borderBottom: showBottomBorder ? `1px solid ${theme.palette.divider}` : "none",
        transition: "background-color 0.15s",
        "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px" },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} noWrap title={composeDocumentLabel(doc)}>
          {composeDocumentLabel(doc)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {size} · {uploaded}
        </Typography>
      </Box>
      {ext !== "—" && (
        <Chip
          label={ext.replace(".", "").toUpperCase()}
          size={UIComponentSize.Small}
          variant="outlined"
          sx={{ bgcolor: alpha(theme.palette.text.primary, 0.04) }}
        />
      )}
      {onPin && (
        <Tooltip
          title={pinned ? "Pinned — opens from the Documents action" : "Pin so the Documents action opens this file"}
        >
          <IconButton
            size="small"
            aria-label={pinned ? "Pinned document" : "Pin document"}
            aria-pressed={pinned}
            onClick={(e) => {
              e.stopPropagation();
              onPin();
            }}
            sx={{ color: pinned ? "primary.main" : "text.disabled" }}
          >
            {pinned ? <PushPin sx={{ fontSize: 16 }} /> : <PushPinOutlined sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete document">
          <IconButton
            size="small"
            aria-label={`Delete ${composeDocumentLabel(doc)}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
          >
            <DeleteOutline sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
